import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components/macro';
import PageSizeSelect, { pageSizeOtions } from './PageSizeSelect';
import { numFormatter } from 'src/utils/formatters';
import * as colors from 'src/styles/colors';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { useStore } from 'src/hooks/useStore';
import { mobilePoint } from 'src/styles/globalStyle';
import { tryParseInt } from 'src/utils/formatters';

const Wrapper = styled.div`
  width: 80%;
  padding: 12px 16px;
  backdrop-filter: blur(50px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  color: ${colors.BlueGray_400};
  font-size: 14px;

  @media (max-width: ${mobilePoint}) {
    display: block;
  }
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: max-content;

  @media (max-width: ${mobilePoint}) {
    width: 100%;
  }
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: ${mobilePoint}) {
    justify-content: center;
  }
`;

const Navigation = styled.div`
  display: flex;
  align-items: center;
  margin-right: 2.5rem;
  gap: 5px;

  @media (max-width: ${mobilePoint}) {
    width: 100%;
    display: flex;
    justify-content: space-around;
    margin-right: 0;
  }
`;

const Label = styled.li`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  user-select: none;

  height: 2rem;
`;

const NavLabel = styled(Label)<{ disabled?: boolean }>`
  :hover {
    cursor: pointer;
  }

  ${(props) =>
    props.disabled &&
    css`
      color: ${colors.BlueGray_300};
      :hover {
        cursor: not-allowed;
      }
    `}
`;

const Text = styled.div`
  white-space: nowrap;
  display: flex;
  align-items: center;
`;

const GoTo = styled.div`
  user-select: none;
  display: flex;
  align-items: center;

  input {
    height: 1.5em;
    width: 3.5rem;
    padding: 6px 12px;
    margin: 0 8px;
    border: 1px solid ${colors.BlueGray_200};
    border-radius: 4px;
    text-align: right;
    font-size: 14px;

    :focus {
      outline: none;
    }
  }

  span {
    margin: 0 8px;
  }
`;

const PerPage = styled.div`
  user-select: none;
  display: flex;
  align-items: center;

  span {
    margin-left: 8px;
  }
`;

const Total = styled.div`
  margin-left: 2.5rem;

  span {
    margin: 0 8px;
  }
  @media (max-width: ${mobilePoint}) {
    margin-left: 0;
    margin-top: 0.5em;
    font-size: 12px;
  }
`;

export const FIRST_PAGE: number = 1;
export const DEFAULT_PAGE_SIZE: number = pageSizeOtions[0].value;

interface IProps {
  totalCount: number;
  totalPages: number;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  pageNo: number;
  setPageNo: (pageNo: number) => void;
  start?: number;
  end?: number;
}

/**
 * 網站 分頁。
 */
const Pagination: React.FC<IProps> = ({
  totalCount,
  totalPages,
  pageSize,
  setPageSize,
  pageNo,
  setPageNo,
  start = (pageNo - 1) * pageSize + 1,
  end = start + tryParseInt(pageSize) - 1,
}) => {
  const isMobile = useStore((state) => state.isMobile);
  const [inputPageNo, setInputPageNo] = useState<number>(pageNo);

  useEffect(() => {
    setInputPageNo(pageNo);
  }, [pageNo]);

  const isFirst = pageNo === FIRST_PAGE;
  const isLast = pageNo === totalPages;

  const toFirst = () => {
    !isFirst && setPageNo(1);
  };

  const toPrev = () => {
    !isFirst && setPageNo(pageNo - 1);
  };

  const toNext = () => {
    !isLast && setPageNo(pageNo + 1);
  };

  const toLast = () => {
    !isLast && setPageNo(totalPages);
  };

  const handleInputPageNoChange = (e: any) => {
    const value = String(e.target.value).trim();
    const num = Number(value);
    if (isNaN(num)) {
      setInputPageNo(FIRST_PAGE);
    } else {
      if (num > totalPages) {
        setInputPageNo(totalPages);
      } else if (num < FIRST_PAGE) {
        setInputPageNo(FIRST_PAGE);
      } else {
        setInputPageNo(num);
      }
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setPageNo(inputPageNo);
  };

  return (
    <Wrapper>
      <Left>
        <Navigation>
          {!isMobile && (
            <NavLabel disabled={isFirst} onClick={toFirst}>
              <Text>第一頁</Text>
            </NavLabel>
          )}
          <NavLabel disabled={isFirst} onClick={toPrev}>
            <Text>
              <FaAngleLeft />
              上一頁
            </Text>
          </NavLabel>
          <GoTo>
            <form onSubmit={handleSubmit}>
              <input
                type="number"
                value={inputPageNo}
                onChange={handleInputPageNoChange}
                onBlur={handleSubmit}
              />
            </form>
            of<span>{totalPages}</span>
          </GoTo>
          <NavLabel disabled={isLast} onClick={toNext}>
            <Text>
              下一頁
              <FaAngleRight />
            </Text>
          </NavLabel>
          {!isMobile && (
            <NavLabel disabled={isLast} onClick={toLast}>
              <Text>最後一頁</Text>
            </NavLabel>
          )}
        </Navigation>
      </Left>
      <Right>
        {!isMobile && (
          <PerPage>
            <PageSizeSelect
              onChange={(size) => {
                setPageSize(size);
                setPageNo(FIRST_PAGE);
              }}
            />
            <span>項目/頁</span>
          </PerPage>
        )}
        <Total>
          <span>
            {start} - {end}
          </span>
          共<span>{numFormatter(totalCount)}</span>項
        </Total>
      </Right>
    </Wrapper>
  );
};

export default Pagination;
